/* Interactive SVG for a Kohd layout.
   - Click a trace or link to select it; drag its round handles to move bends,
     drag a hollow midpoint handle to add a bend, double-click or double-tap a bend to drop it.
   - On a word trace, drag a diamond end handle to slide that end around its ring.
   - Drag a word's node to move the whole glyph (links re-route on drop).
   - Drag the background to pan, wheel or pinch to zoom.
   The circuit itself comes from draw.ts; this file adds the hit targets
   (data-kohd-hit, data-kohd-node), bend handles (data-kohd-handle) and pointer logic.
   Import KohdCanvas.css once for the cursors, halos and handle colors. */

import { PointerEvent as ReactPointerEvent, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { KohdLayout, LinkShape, LATTICE, Pt, RingEnd, TraceShape } from '../layout';
import { bendsOf, traceAnchor, TraceEdit } from '../edits';
import { drawLayout } from '../draw';
import KohdShapes from './KohdShapes';
import './KohdCanvas.css';

export interface View {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface KohdCanvasProps {
  layout: KohdLayout;
  view: View;
  onView: (v: View) => void;
  selected: string | null;
  onSelect: (key: string | null) => void;
  /** Called once before a drag changes anything, so the page can record undo history. */
  onBeginEdit: () => void;
  /** Edit for a trace or link (bends relative to its anchor, ring end angles); merge it into any existing edit. */
  onEdit: (key: string, edit: TraceEdit) => void;
  /** Commits a word drag by this offset. */
  onMoveWord: (key: string, delta: Pt) => void;
  showLabels: boolean;
  svgRef: RefObject<SVGSVGElement | null>;
}

type Drag =
  | { kind: 'pan'; client: Pt; view: View; moved: boolean }
  | { kind: 'bend'; key: string; index: number; points: Pt[]; anchor: Pt; started: boolean }
  | { kind: 'end'; key: string; which: 'start' | 'end'; ring: RingEnd; bends: Pt[]; started: boolean }
  | { kind: 'word'; key: string; start: Pt; delta: Pt; moved: boolean }
  | { kind: 'pick'; key: string }
  | { kind: 'pinch'; dist: number; mid: Pt; view: View };

const poly = (pts: Pt[]) => pts.map((p) => `${p.x},${p.y}`).join(' ');
const snap = (v: number) => Math.round(v / LATTICE) * LATTICE;
const MOVE_THRESHOLD = 4;
const DOUBLE_PRESS_MS = 400;
const RING_STEP = Math.PI / 12; // trace ends snap to 15 degree steps around their ring

/** Wide invisible strokes and discs that make thin lines easy to grab. */
function HitTargets({ layout }: { layout: KohdLayout }) {
  return (
    <g data-kohd-ui="">
      {layout.rows.map((row) => (
        <g key={row.text}>
          {row.links.map((l) => <polyline key={l.key} points={poly(l.points)} className="kohd-hit" data-kohd-hit={l.key} />)}
          {row.words.map((w) => (
            <g key={w.key}>
              {w.traces.map((t) => <polyline key={t.key} points={poly(t.points)} className="kohd-hit" data-kohd-hit={t.key} />)}
              {w.nodes.map((n) => <circle key={`${n.col},${n.row}`} cx={n.x} cy={n.y} r={n.outerR + 4} className="kohd-node-hit" data-kohd-node={w.key} />)}
            </g>
          ))}
        </g>
      ))}
    </g>
  );
}

/** Dashed guides on a word trace's two rings, with a diamond handle at each end. */
function TraceEnds({ trace, size }: { trace: TraceShape; size: number }) {
  const ends: Array<['start' | 'end', Pt]> = [['start', trace.points[0]], ['end', trace.points[trace.points.length - 1]]];
  return (
    <>
      {[trace.from, trace.to].map((ring, i) => <circle key={i} cx={ring.x} cy={ring.y} r={ring.r} className="kohd-ring-guide" />)}
      {ends.map(([which, p]) => (
        <rect
          key={which}
          x={p.x - size}
          y={p.y - size}
          width={size * 2}
          height={size * 2}
          transform={`rotate(45 ${p.x} ${p.y})`}
          className="kohd-handle kohd-handle--end"
          data-kohd-handle={which}
        />
      ))}
    </>
  );
}

function findPath(layout: KohdLayout, key: string): TraceShape | LinkShape | undefined {
  for (const row of layout.rows) {
    const link = row.links.find((l) => l.key === key);
    if (link) return link;
    for (const w of row.words) {
      const t = w.traces.find((tr) => tr.key === key);
      if (t) return t;
    }
  }
  return undefined;
}

export default function KohdCanvas(props: KohdCanvasProps) {
  const { layout, view, onView, selected, onSelect, onBeginEdit, onEdit, onMoveWord, showLabels, svgRef } = props;
  const [drag, setDrag] = useState<Drag | null>(null);
  const pointers = useRef(new Map<number, Pt>()); // active touches, for two-finger pinch zoom
  const lastBendPress = useRef<{ key: string; index: number; time: number } | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  // Wheel zoom needs a non-passive listener so the page does not scroll.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = viewRef.current;
      const p = clientToSvg(svg, v, e.clientX, e.clientY);
      const k = Math.min(4, Math.max(0.25, Math.exp(e.deltaY * 0.0015)));
      onView({ x: p.x - (p.x - v.x) * k, y: p.y - (p.y - v.y) * k, w: v.w * k, h: v.h * k });
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [svgRef, onView]);

  const toSvg = (e: { clientX: number; clientY: number }) => clientToSvg(svgRef.current, view, e.clientX, e.clientY);

  const capture = (e: ReactPointerEvent) => {
    try { svgRef.current?.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
  };

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button > 0) return;
    const target = e.target as Element;
    const handle = target.closest('[data-kohd-handle]');
    const hit = target.closest('[data-kohd-hit]');
    const node = target.closest('[data-kohd-node]');
    capture(e);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      setDrag({ kind: 'pinch', dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, mid: toSvg({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 }), view });
      return;
    }
    if (handle && selected) {
      const path = findPath(layout, selected);
      const anchor = traceAnchor(layout, selected);
      if (!path || !anchor) return;
      const role = handle.getAttribute('data-kohd-handle');
      if ((role === 'start' || role === 'end') && 'from' in path) {
        setDrag({ kind: 'end', key: selected, which: role, ring: role === 'start' ? path.from : path.to, bends: bendsOf(path.points, anchor), started: false });
        return;
      }
      const index = Number(handle.getAttribute('data-index'));
      if (role === 'bend') {
        // Pointer capture sends the native dblclick to the svg, so a second press on the same bend removes it (double-tap on touch too).
        const last = lastBendPress.current;
        if (last && last.key === selected && last.index === index && e.timeStamp - last.time < DOUBLE_PRESS_MS) {
          lastBendPress.current = null;
          removeBend(index);
          return;
        }
        lastBendPress.current = { key: selected, index, time: e.timeStamp };
      }
      const points = [...path.points];
      if (role === 'mid') points.splice(index, 0, points[index]);
      setDrag({ kind: 'bend', key: selected, index, points, anchor, started: false });
    } else if (node) {
      setDrag({ kind: 'word', key: node.getAttribute('data-kohd-node')!, start: toSvg(e), delta: { x: 0, y: 0 }, moved: false });
    } else if (hit) {
      setDrag({ kind: 'pick', key: hit.getAttribute('data-kohd-hit')! });
    } else {
      setDrag({ kind: 'pan', client: { x: e.clientX, y: e.clientY }, view, moved: false });
    }
  };

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!drag) return;
    if (drag.kind === 'pinch') {
      if (pointers.current.size < 2) return;
      const [a, b] = Array.from(pointers.current.values());
      const k = drag.dist / (Math.hypot(a.x - b.x, a.y - b.y) || 1);
      const { mid, view: v } = drag;
      onView({ x: mid.x - (mid.x - v.x) * k, y: mid.y - (mid.y - v.y) * k, w: v.w * k, h: v.h * k });
      return;
    }
    if (drag.kind === 'pan') {
      const dx = e.clientX - drag.client.x;
      const dy = e.clientY - drag.client.y;
      if (!drag.moved && Math.hypot(dx, dy) < MOVE_THRESHOLD) return;
      const unitsPerPx = svgUnitsPerPx(svgRef.current, drag.view);
      onView({ ...drag.view, x: drag.view.x - dx * unitsPerPx, y: drag.view.y - dy * unitsPerPx });
      if (!drag.moved) setDrag({ ...drag, moved: true });
    } else if (drag.kind === 'bend') {
      const p = toSvg(e);
      const points = drag.points.map((q, i) => (i === drag.index ? { x: snap(p.x), y: snap(p.y) } : q));
      if (!drag.started) onBeginEdit();
      onEdit(drag.key, { bends: bendsOf(points, drag.anchor) });
      setDrag({ ...drag, points, started: true });
    } else if (drag.kind === 'end') {
      const p = toSvg(e);
      const angle = Math.round(Math.atan2(p.y - drag.ring.y, p.x - drag.ring.x) / RING_STEP) * RING_STEP;
      if (!drag.started) {
        onBeginEdit();
        setDrag({ ...drag, started: true });
      }
      onEdit(drag.key, drag.which === 'start' ? { bends: drag.bends, start: angle } : { bends: drag.bends, end: angle });
    } else if (drag.kind === 'word') {
      const p = toSvg(e);
      const delta = { x: snap(p.x - drag.start.x), y: snap(p.y - drag.start.y) };
      if (delta.x !== drag.delta.x || delta.y !== drag.delta.y) setDrag({ ...drag, delta, moved: true });
    }
  };

  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (!drag) return;
    if (drag.kind === 'pinch') { setDrag(null); return; }
    if (drag.kind === 'pick') onSelect(drag.key);
    if (drag.kind === 'pan' && !drag.moved) onSelect(null);
    if (drag.kind === 'word') {
      if (drag.moved && (drag.delta.x !== 0 || drag.delta.y !== 0)) {
        onBeginEdit();
        onMoveWord(drag.key, drag.delta);
      }
      onSelect(drag.key);
    }
    setDrag(null);
  };

  const removeBend = (index: number) => {
    if (!selected) return;
    const path = findPath(layout, selected);
    const anchor = traceAnchor(layout, selected);
    if (!path || !anchor) return;
    onBeginEdit();
    onEdit(selected, { bends: bendsOf(path.points.filter((_, i) => i !== index), anchor) });
  };

  const dragged = drag?.kind === 'word' ? drag : null;
  const shapes = useMemo(
    () => drawLayout(layout, { labels: showLabels, selected, offsets: dragged ? { [dragged.key]: dragged.delta } : undefined }),
    [layout, showLabels, selected, dragged],
  );
  const selectedPath = selected ? findPath(layout, selected) : undefined;
  const handleR = Math.max(5, view.w / 180);

  return (
    <svg
      ref={svgRef}
      className={`kohd-svg${drag?.kind === 'pan' && drag.moved ? ' is-panning' : ''}`}
      viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Kohd circuit"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={(e) => { pointers.current.delete(e.pointerId); setDrag(null); }}
    >
      <KohdShapes shapes={shapes} />
      <HitTargets layout={layout} />
      {selectedPath && (
        <g data-kohd-ui="" className="kohd-handles">
          {selectedPath.points.slice(1).map((p, i) => {
            const a = selectedPath.points[i];
            return <circle key={`m${i}`} cx={(a.x + p.x) / 2} cy={(a.y + p.y) / 2} r={handleR * 0.75} className="kohd-handle kohd-handle--mid" data-kohd-handle="mid" data-index={i + 1} />;
          })}
          {selectedPath.points.slice(1, -1).map((p, i) => (
            <circle key={`v${i}`} cx={p.x} cy={p.y} r={handleR} className="kohd-handle" data-kohd-handle="bend" data-index={i + 1} />
          ))}
          {'from' in selectedPath && <TraceEnds trace={selectedPath} size={handleR} />}
        </g>
      )}
    </svg>
  );
}

function svgUnitsPerPx(svg: SVGSVGElement | null, view: View): number {
  const rect = svg?.getBoundingClientRect();
  if (!rect || rect.width === 0 || rect.height === 0) return 1;
  return Math.max(view.w / rect.width, view.h / rect.height);
}

/** Client (screen) point to SVG user space for a meet-scaled, centered viewBox. */
function clientToSvg(svg: SVGSVGElement | null, view: View, clientX: number, clientY: number): Pt {
  const rect = svg?.getBoundingClientRect();
  if (!rect || rect.width === 0 || rect.height === 0) return { x: view.x, y: view.y };
  const k = svgUnitsPerPx(svg, view);
  const padX = (rect.width * k - view.w) / 2;
  const padY = (rect.height * k - view.h) / 2;
  return { x: view.x - padX + (clientX - rect.left) * k, y: view.y - padY + (clientY - rect.top) * k };
}
